import {
  Box,
  Grid,
  Typography,
  FormHelperText,
  IconButton,
} from "@mui/material";
import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import StepperGuide from "src/components/StepperGuide";
import { ArrowLeft } from "react-feather";
import QuestionMultipleChoiceManyAnswer from "./components/QuestionMultipleChoiceManyAnswer";
import QuestionMultipleChoiceSingleAnswer from "./components/QuestionMultipleChoiceSingleAnswer";
import QuestionTrueOrFalse from "./components/QuestionTrueOrFalse";
import QuestionYesOrNo from "./components/QuestionYesOrNo";
import QuestionNPS from "./components/QuestionNPS";
import QuestionShortAnswer from "./components/QuestionShortAnswer";
import QuestionEssayAnswer from "./components/QuestionEssayAnswer";

const SurveyInBookingForm = ({
  errors,
  control,
  randomErrors,
  setValue,
  getValues,
  stepperGuideLength,
  handleBack,
  _display = "default",
  surveysWithBookingFirst,
  customStyle,
}) => {
  const { t } = useTranslation();

  const handleHasRequired = () => {
    if (surveysWithBookingFirst.length) {
      surveysWithBookingFirst.forEach((details) => {
        if (details?.questionslist.length > 0) {
          details?.questionslist.forEach((item) => {
            let field = "";
            if (item.type === "question-short-answer") {
              field = `answer[_${item.id}][_${item.choices[0].id}]`;
            }

            if (item.type === "question-comment-essay") {
              field = `answer[_${item.id}][_${item.choices[0].id}]`;
            }

            if (item.type === "question-one-answer") {
              field = `answer[_${item.id}]`;
            }

            if (item.type === "question-multiple-answer") {
              field = `answer[_${item.id}]`;
            }

            if (item.type === "question-true-false") {
              field = `answer[_${item.id}]`;
            }

            if (item.type === "question-yes-no") {
              field = `answer[_${item.id}]`;
            }

            if (item.type === "question-nps") {
              field = `answer[_${item.id}]`;
            }

            let currentValue = getValues(field) ?? "";

            setValue(field, currentValue);
          });
        }
      });
    }
  };

  const isViewOnly =
    new URLSearchParams(window.location.search).get("viewOnly") === "1";

  React.useEffect(() => {
    handleHasRequired();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isIframe = window.self !== window.top;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
          mb: "50px",
          mt: "58px",
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: "20px",
            }}
          >
            {(_display === "embeded" || isIframe) && !isViewOnly && (
              <Box>
                <IconButton onClick={() => handleBack()}>
                  <ArrowLeft />
                </IconButton>
              </Box>
            )}

            <Box>
              <Typography
                sx={{
                  fontFamily: `var(--font-${customStyle?.headingFont})`,
                  fontSize: "18px",
                  lineHeight: "23px",
                  alignItems: "center",
                  color: customStyle?.textColor ?? "var(--color-black)",
                }}
              >
                {t("individual.booking.schedule.survey")}
              </Typography>

              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "18px",
                  color: customStyle?.secondaryTextColor ?? "#878787",
                  fontFamily: `var(--font-${customStyle?.bodyFont})`,
                }}
              >
                {t("individual.booking.schedule.survey.take_to_continue")}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box>
          <StepperGuide
            length={stepperGuideLength}
            activeLength={[1, 2, 3]}
            customStyle={customStyle}
          />
        </Box>
      </Box>

      {surveysWithBookingFirst.map((details, surveyKey) => {
        return (
          <Fragment key={surveyKey}>
            <Grid item xs={12}>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "24px",
                    textTransform: "capitalize",
                    color: customStyle?.textColor ?? "var(--color-black)",
                    fontFamily: `var(--font-${customStyle?.bodyFont})`,
                  }}
                >
                  {details.name}
                </Typography>
              </Box>

              <Box>
                {details?.questionslist.length > 0 ? (
                  <>
                    {details?.questionslist.map((item, key) => {
                      return (
                        <Box
                          key={key}
                          sx={{
                            mt: "23px",
                            mb: 3,
                            border: "1px solid #D9DBE9",
                            ...(customStyle?.borderColor && {
                              border: `1px solid ${customStyle?.borderColor}`,
                            }),
                            borderRadius: "8px",
                            padding: "25px 28px",
                            backgroundColor: "#FFF",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 400,
                                fontSize: "14px",
                                lineHeight: "24px",
                                fontFamily: `var(--font-${customStyle?.bodyFont})`,
                                color: customStyle?.textColor ?? "#19181B",
                              }}
                            >
                              {key + 1}. {item.question}{" "}
                              {Boolean(parseInt(item.is_required)) && (
                                <span style={{ color: "red" }}> * </span>
                              )}
                            </Typography>
                          </Box>
                          <Box>
                            {Boolean(
                              errors["answer"]?.[`_${item.id}`]?.required
                            ) && (
                              <Box>
                                <FormHelperText error>
                                  {errors["answer"]?.[`_${item.id}`]?.message}
                                </FormHelperText>
                              </Box>
                            )}
                          </Box>
                          {/* error display here */}
                          <Box>
                            {Boolean(
                              randomErrors?.[`${item.id}`]?.hasError
                            ) && (
                              <FormHelperText error>
                                {randomErrors?.[`${item.id}`]?.message}{" "}
                              </FormHelperText>
                            )}
                          </Box>

                          {/* question-short-answer */}
                          {item.type === "question-short-answer" && (
                            <Box mt={1}>
                              <QuestionShortAnswer
                                setValue={setValue}
                                control={control}
                                item={item}
                                errors={errors}
                              />
                            </Box>
                          )}

                          {/* question-comment-essay */}
                          {item.type === "question-comment-essay" && (
                            <Box mt={1}>
                              <QuestionEssayAnswer
                                control={control}
                                item={item}
                                errors={errors}
                              />
                            </Box>
                          )}

                          {/* question-one-answer */}
                          {item.type === "question-one-answer" && (
                            <Box mx={5} mt={1}>
                              <QuestionMultipleChoiceSingleAnswer
                                setValue={setValue}
                                control={control}
                                item={item}
                                customStyle={customStyle}
                              />
                            </Box>
                          )}

                          {/* question-multiple-answer */}
                          {item.type === "question-multiple-answer" && (
                            <Box mx={5} mt={1}>
                              <QuestionMultipleChoiceManyAnswer
                                setValue={setValue}
                                control={control}
                                item={item}
                                customStyle={customStyle}
                              />
                            </Box>
                          )}

                          {/* question-true-false */}
                          {item.type === "question-true-false" && (
                            <Box mx={5} mt={1}>
                              <QuestionTrueOrFalse
                                setValue={setValue}
                                control={control}
                                item={item}
                                customStyle={customStyle}
                              />
                            </Box>
                          )}

                          {/* question-yes-no */}
                          {item.type === "question-yes-no" && (
                            <Box mx={5} mt={1}>
                              <QuestionYesOrNo
                                setValue={setValue}
                                control={control}
                                item={item}
                                customStyle={customStyle}
                              />
                            </Box>
                          )}

                          {/* question-nps */}
                          {item.type === "question-nps" && (
                            <Box>
                              <QuestionNPS
                                setValue={setValue}
                                control={control}
                                item={item}
                                customStyle={customStyle}
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </>
                ) : (
                  <Typography sx={{ color: "var(--color-gray-700)" }}>
                    {t("survey.take.form.error.noquestion_found")}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Fragment>
        );
      })}
    </>
  );
};

export default SurveyInBookingForm;
